import React, { useEffect, useState } from 'react'
import {
  Alert,
  Button,
  Col,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from 'reactstrap'
import { api } from '../../utils'
import { notify } from '../../utils/notify'

const RECOVERY_MODE_SINGLE = 'single'
const RECOVERY_MODE_RANGE = 'range'

const summaryCardStyle = {
  minHeight: '100%',
  padding: '12px 16px',
  border: '1px solid #e8edf4',
  borderRadius: '12px',
  backgroundColor: '#f8fbff',
}

const summaryLabelStyle = {
  marginBottom: '6px',
  fontSize: '0.75rem',
  fontWeight: 700,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: '#52627a',
}

const summaryValueStyle = {
  color: '#4c5d73',
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
}

const optionLabelStyle = {
  display: 'block',
  marginBottom: '12px',
  fontSize: '0.9rem',
  fontWeight: 700,
  letterSpacing: '0.02em',
  textTransform: 'uppercase',
  color: '#4c5d73',
}

function normalizeMonthValue(value) {
  if (!value) {
    return ''
  }

  return value.slice(0, 7)
}

function toApiReferenceMonth(value) {
  const normalizedValue = normalizeMonthValue(value)
  return normalizedValue ? `${normalizedValue}-01` : ''
}

function formatRecoveryError(error) {
  const responseData = error?.response?.data

  if (typeof responseData === 'string' && responseData.trim()) {
    return responseData
  }

  if (responseData?.error) {
    return responseData.error
  }

  if (responseData && typeof responseData === 'object') {
    const firstValue = Object.values(responseData)[0]
    if (Array.isArray(firstValue) && firstValue.length > 0) {
      return firstValue[0]
    }
    if (typeof firstValue === 'string' && firstValue.trim()) {
      return firstValue
    }
  }

  return 'Impossibile pianificare il recupero volumi.'
}

function buildRecoveryOutcomeMessage(data) {
  const summary = []

  if (data?.created_count) {
    summary.push(`${data.created_count} month${data.created_count === 1 ? '' : 's'} scheduled`)
  }
  if (data?.skipped_count) {
    summary.push(`${data.skipped_count} skipped`)
  }
  if (data?.blocked_count) {
    summary.push(`${data.blocked_count} blocked`)
  }

  if (summary.length === 0) {
    return {
      kind: 'warning',
      message: 'No recovery months were scheduled.',
    }
  }

  return {
    kind: data?.skipped_count || data?.blocked_count ? 'warning' : 'success',
    message: `Recovery result: ${summary.join(' | ')}.`,
  }
}

function VolumeRetrieveHistoricalRecovery({ isOpen, onClose, scheduleId, initialDetails = null }) {
  const [volumeRetrieve, setVolumeRetrieve] = useState(initialDetails)
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState(null)
  const [recoveryMode, setRecoveryMode] = useState(RECOVERY_MODE_RANGE)
  const [recoveryMonth, setRecoveryMonth] = useState('')
  const [recoveryStartMonth, setRecoveryStartMonth] = useState('')
  const [recoveryEndMonth, setRecoveryEndMonth] = useState('')
  const [recoveryForce, setRecoveryForce] = useState(false)
  const [recoverySubmitting, setRecoverySubmitting] = useState(false)

  useEffect(() => {
    if (!isOpen) {
      setLoadError(null)
      setRecoveryMode(RECOVERY_MODE_RANGE)
      setRecoveryMonth('')
      setRecoveryStartMonth('')
      setRecoveryEndMonth('')
      setRecoveryForce(false)
      setRecoverySubmitting(false)
      return
    }

    if (!scheduleId) {
      setVolumeRetrieve(null)
      return
    }

    if (initialDetails) {
      setVolumeRetrieve(initialDetails)
      setLoading(false)
      return
    }

    let isMounted = true

    async function load() {
      setLoading(true)
      setLoadError(null)

      try {
        const response = await api.get(`/market-screener-detail/${scheduleId}`)
        if (isMounted) {
          setVolumeRetrieve(response.data)
        }
      } catch (error) {
        if (isMounted) {
          setLoadError('Error fetching market screener details.')
        }
      } finally {
        if (isMounted) {
          setLoading(false)
        }
      }
    }

    load()

    return () => {
      isMounted = false
    }
  }, [initialDetails, isOpen, scheduleId])

  const handleClose = () => {
    if (recoverySubmitting) {
      return
    }
    onClose()
  }

  const validateRecoverySelection = () => {
    if (recoveryMode === RECOVERY_MODE_SINGLE) {
      if (!recoveryMonth) {
        notify('Select a recovery month before continuing.', 'error')
        return false
      }

      return true
    }

    if (!recoveryStartMonth || !recoveryEndMonth) {
      notify('Select both recovery months before continuing.', 'error')
      return false
    }

    if (recoveryEndMonth < recoveryStartMonth) {
      notify('Recovery end month must be greater than or equal to the start month.', 'error')
      return false
    }

    return true
  }

  const handleRecoveryModeChange = (mode) => {
    setRecoveryMode(mode)
    if (mode === RECOVERY_MODE_SINGLE) {
      setRecoveryStartMonth('')
      setRecoveryEndMonth('')
      return
    }

    setRecoveryMonth('')
  }

  const submitRecovery = async () => {
    if (recoverySubmitting || !scheduleId || !validateRecoverySelection()) {
      return
    }

    setRecoverySubmitting(true)

    try {
      const payload =
        recoveryMode === RECOVERY_MODE_SINGLE
          ? {
              reference_month: toApiReferenceMonth(recoveryMonth),
              force: recoveryForce,
            }
          : {
              reference_month_start: toApiReferenceMonth(recoveryStartMonth),
              reference_month_end: toApiReferenceMonth(recoveryEndMonth),
              force: recoveryForce,
            }

      const response = await api.post(
        `volume-report-scheduled/${scheduleId}/recover-month/`,
        payload
      )
      const outcome = buildRecoveryOutcomeMessage(response?.data)
      notify(outcome.message, outcome.kind)
      onClose()
    } catch (error) {
      notify(formatRecoveryError(error), 'error')
    } finally {
      setRecoverySubmitting(false)
    }
  }

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="xl" centered>
      <ModalHeader toggle={handleClose}>Historical Recovery</ModalHeader>
      <ModalBody>
        {loading ? <Spinner /> : null}
        {!loading && loadError ? <div className="text-center text-danger">{loadError}</div> : null}
        {!loading && !loadError ? (
          <>
            <Alert color="info" fade={false}>
              {recoveryMode === RECOVERY_MODE_SINGLE
                ? 'Recover a single historical month for this Market Screener.'
                : 'All months between start and end are included. The range is inclusive.'}
            </Alert>
            <Row className="mb-4">
              <Col xs={12} md={6} xl={4} className="mb-3 mb-xl-0">
                <div style={summaryCardStyle}>
                  <div style={summaryLabelStyle}>Name</div>
                  <div style={summaryValueStyle}>{volumeRetrieve?.name || '-'}</div>
                </div>
              </Col>
              <Col xs={12} md={6} xl={4} className="mb-3 mb-xl-0">
                <div style={summaryCardStyle}>
                  <div style={summaryLabelStyle}>Cliente</div>
                  <div style={summaryValueStyle}>{volumeRetrieve?.cliente?.nome || '-'}</div>
                </div>
              </Col>
              <Col xs={12} xl={4}>
                <div style={summaryCardStyle}>
                  <div style={summaryLabelStyle}>Locale</div>
                  <div style={summaryValueStyle}>{volumeRetrieve?.locale || '-'}</div>
                </div>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <FormGroup tag="fieldset" className="mb-4">
                  <Label style={optionLabelStyle}>Recovery mode</Label>
                  <div className="d-flex" style={{ gap: '24px', flexWrap: 'wrap' }}>
                    <div className="custom-control custom-radio custom-control-inline">
                      <Input
                        className="custom-control-input"
                        type="radio"
                        id="recoveryModeSingle"
                        name="recoveryMode"
                        checked={recoveryMode === RECOVERY_MODE_SINGLE}
                        onChange={() => handleRecoveryModeChange(RECOVERY_MODE_SINGLE)}
                      />
                      <label className="custom-control-label" htmlFor="recoveryModeSingle">
                        Single month
                      </label>
                    </div>
                    <div className="custom-control custom-radio custom-control-inline">
                      <Input
                        className="custom-control-input"
                        type="radio"
                        id="recoveryModeRange"
                        name="recoveryMode"
                        checked={recoveryMode === RECOVERY_MODE_RANGE}
                        onChange={() => handleRecoveryModeChange(RECOVERY_MODE_RANGE)}
                      />
                      <label className="custom-control-label" htmlFor="recoveryModeRange">
                        Month range
                      </label>
                    </div>
                  </div>
                </FormGroup>
              </Col>
            </Row>
            <Row>
              {recoveryMode === RECOVERY_MODE_SINGLE ? (
                <Col xs={12} md={6} xl={4}>
                  <FormGroup>
                    <Label for="recoveryMonth" style={{ display: 'block' }}>
                      Recovery month
                    </Label>
                    <Input
                      className="form-control-alternative"
                      type="month"
                      name="recoveryMonth"
                      id="recoveryMonth"
                      value={recoveryMonth}
                      onChange={(event) => setRecoveryMonth(normalizeMonthValue(event.target.value))}
                    />
                  </FormGroup>
                </Col>
              ) : null}
              {recoveryMode === RECOVERY_MODE_RANGE ? (
                <>
                  <Col xs={12} md={6} xl={4}>
                    <FormGroup>
                      <Label for="recoveryStartMonth" style={{ display: 'block' }}>
                        Recovery start month
                      </Label>
                      <Input
                        className="form-control-alternative"
                        type="month"
                        name="recoveryStartMonth"
                        id="recoveryStartMonth"
                        value={recoveryStartMonth}
                        onChange={(event) => setRecoveryStartMonth(normalizeMonthValue(event.target.value))}
                      />
                    </FormGroup>
                  </Col>
                  <Col xs={12} md={6} xl={4}>
                    <FormGroup>
                      <Label for="recoveryEndMonth" style={{ display: 'block' }}>
                        Recovery end month
                      </Label>
                      <Input
                        className="form-control-alternative"
                        type="month"
                        name="recoveryEndMonth"
                        id="recoveryEndMonth"
                        value={recoveryEndMonth}
                        onChange={(event) => setRecoveryEndMonth(normalizeMonthValue(event.target.value))}
                      />
                    </FormGroup>
                  </Col>
                </>
              ) : null}
              <Col xs={12} xl={4}>
                <FormGroup>
                  <div style={optionLabelStyle}>Overwrite behavior</div>
                  <div className="custom-control custom-checkbox">
                    <Input
                      className="custom-control-input"
                      type="checkbox"
                      id="recoveryForce"
                      checked={recoveryForce}
                      onChange={(event) => setRecoveryForce(event.target.checked)}
                    />
                    <label className="custom-control-label" htmlFor="recoveryForce">
                      Force historical overwrite
                    </label>
                  </div>
                </FormGroup>
              </Col>
            </Row>
          </>
        ) : null}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" type="button" onClick={handleClose} disabled={recoverySubmitting}>
          Cancel
        </Button>
        <Button
          color="info"
          type="button"
          onClick={submitRecovery}
          disabled={loading || !!loadError || recoverySubmitting}
        >
          {recoverySubmitting
            ? 'Submitting...'
            : recoveryMode === RECOVERY_MODE_SINGLE
            ? 'Recover month'
            : 'Recover month range'}
        </Button>
      </ModalFooter>
    </Modal>
  )
}

export default VolumeRetrieveHistoricalRecovery
